// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@flip/App/Components/Common/XngZone";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { constants } from "@flip/XDL/constants";
import Filter from "@flip/XDL/Icons/Filter";
import MenuFocused from "@flip/XDL/Icons/MenuFocused";
import {
    addScrollbarGutter,
    color,
    hideElement,
    removeScrollbarGutter,
    styledScrollbar,
    xScreen,
} from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { Clickable, Hidden, Icon, Typography } from "@insite/mobius/index";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return { isTabletScreen: state.xCommon.isTabletScreen };
};

const mapDispatchToProps = {
    clearAllProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export interface ProductListLayoutStyles {
    container?: InjectableCss;
    leftColumnContainer?: InjectableCss;
    rightColumnContainer?: InjectableCss;
    suggestionSectionContainer?: InjectableCss;
    productTitleSuggestionWrapper?: InjectableCss;
    actionsSectionContainer?: InjectableCss;
    categorySectionWrapper?: InjectableCss;
    categoryDetailWrapper?: InjectableCss;
    actions?: InjectableCss;
    actionsRight?: InjectableCss;
    paginationBottom?: InjectableCss;
    viewItemWrapper?: InjectableCss;
    paginationList?: InjectableCss;
    countItemContainer?: InjectableCss;
    sortFilterButton?: ButtonPresentationProps;
    filterTabletScreen?: InjectableCss;
    filterTabletScreenContainer?: InjectableCss;
    filterTabletScreenHeader?: InjectableCss;
    filterTabletScreenActions?: InjectableCss;
    refineResultsTitle?: TypographyPresentationProps;
    modalCloseButton?: IconPresentationProps;
}

const sortAndFilterBreakpoint = constants.productList.xSortAndFilter.breakpoint;
export const layoutStyles: ProductListLayoutStyles = {
    container: {
        css: css`
            display: flex;
            gap: 54px;
        `,
    },
    leftColumnContainer: {
        css: css`
            flex: 0 0 285px;
            ${xScreen("lte", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
        `,
    },
    rightColumnContainer: {
        css: css`
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    suggestionSectionContainer: {
        css: css`
            width: 100%;
        `,
    },
    actionsSectionContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: no-wrap;
            padding-top: 6px;
            column-gap: 32px;
            margin-top: 12px;
            border-top: 1px solid ${color("common.border")};
            ${xScreen("lte", 1042)} {
                column-gap: 15px;
            }
            ${xScreen("gt", sortAndFilterBreakpoint)} {
                margin-top: 24px;
                padding-top: 12px;
            }

            ${xScreen("lt", "sm")} {
                margin-top: 10px;
                padding-top: 10px;
            }
        `,
    },

    categorySectionWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            justify-content: flex-start;
            column-gap: 32px;

            > div {
                width: auto;
            }

            > div:empty {
                display: none;
            }

            h2 {
                margin: 0;
            }
        `,
    },
    categoryDetailWrapper: {
        css: css`
            > div {
                margin-top: 24px;
                &:empty {
                    display: none;
                }
            }
        `,
    },
    actions: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            max-width: 365px;
            justify-content: space-between;
            ${xScreen("lte", "xs")} {
                max-width: 100%;
                column-gap: 24px;
                justify-content: flex-start;
            }
        `,
    },
    actionsRight: {
        css: css`
            display: flex;
            align-items: center;
            column-gap: 15px;
            max-width: 355px;
        `,
    },
    paginationBottom: {
        css: css`
            margin-top: 10px;
        `,
    },
    viewItemWrapper: {
        css: css`
            ${xScreen("lte", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
        `,
    },
    paginationList: {
        css: css`
            ${xScreen("lte", 625)} {
                ${hideElement}
            }
        `,
    },
    countItemContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 24px;

            ${xScreen("gt", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
        `,
    },
    sortFilterButton: {
        variant: "secondary",
        sizeVariant: "medium",
        icon: {
            src: Filter,
            position: "left",
        },
        css: css`
            display: flex;
            align-items: center;
            padding-left: 12px;
            padding-right: 12px;
            margin-right: 12px;

            ${TypographyStyle} {
                color: ${color("primary")};
                white-space: nowrap;
            }

            ${IconWrapper} {
                margin-right: 8px;
            }
        `,
    },
    productTitleSuggestionWrapper: {
        css: css`
            width: 100%;
        `,
    },
    filterTabletScreen: {
        css: css`
            background: ${color("common.ui7", 0.33)};
            position: fixed;
            left: 0;
            top: 0;
            z-index: 9999;
            height: 100%;
            width: 100%;
        `,
    },
    filterTabletScreenContainer: {
        css: css`
            background: ${color("common.ui1")};
            position: fixed;
            left: 0;
            top: 0;
            padding: 20px 16px;
            height: 100%;
            max-width: 360px;
            width: 100%;
            ${styledScrollbar(8)};
            ${xScreen("lte", "xs")} {
                max-width: none;
            }
        `,
    },
    filterTabletScreenHeader: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;

            h5 {
                margin-bottom: 0;
            }
        `,
    },
    filterTabletScreenActions: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 20px;
        `,
    },
    refineResultsTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 12px;
        `,
    },
};

const styles = layoutStyles;

const ProductListLayout: FC<Props> = ({ id, isTabletScreen, clearAllProductFilters }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const toggleFilterListModalHandler = (isOpen: boolean) => {
        setModalIsOpen(isOpen);

        if (isOpen) {
            addScrollbarGutter();
            ariaAppHider.hide(document.body);
        } else {
            removeScrollbarGutter();
            ariaAppHider.show(document.body);
        }
    };

    useEffect(() => {
        if (!isTabletScreen) {
            setModalIsOpen(false);
        }
    }, [isTabletScreen]);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftColumnContainer}>
                <Typography {...styles.refineResultsTitle} data-test-selector="productListFilterList">
                    {translate("Refine Results")}
                </Typography>
                <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
            </StyledWrapper>
            <StyledWrapper {...styles.rightColumnContainer}>
                <StyledWrapper {...styles.categorySectionWrapper}>
                    <XngZone contentId={id} zoneName="ContentCategorySection" hint="Categories Section" />
                    <Hidden below="lg">
                        <XngZone contentId={id} zoneName="ContentMainHeader00" hint="Count Item" normalWidth={true} />
                    </Hidden>
                </StyledWrapper>
                <StyledWrapper {...styles.categoryDetailWrapper}>
                    <XngZone
                        contentId={id}
                        zoneName="ContentCategoryContent"
                        hint="Category Detail"
                        normalWidth={true}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.suggestionSectionContainer}>
                    <StyledWrapper
                        {...styles.productTitleSuggestionWrapper}
                        data-test-selector="productTitle-and-suggestion-productList"
                    >
                        <XngZone contentId={id} zoneName="ContentMainHeader06" hint="Suggestion" normalWidth={true} />
                        <StyledWrapper {...styles.countItemContainer}>
                            <Button onClick={() => toggleFilterListModalHandler(true)} {...styles.sortFilterButton}>
                                {translate("Refine Results")}
                            </Button>
                            {createWidgetElement("ProductList/ProductListProductCount", { fields: {} })}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.actionsSectionContainer}>
                    <StyledWrapper {...styles.actions}>
                        <XngZone contentId={id} zoneName="ContentMainHeader01" normalWidth={true} hint="Sort Item" />
                        <XngZone
                            contentId={id}
                            zoneName="ContentMainHeader04"
                            normalWidth={true}
                            hint="Pagination PerPage"
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.actionsRight}>
                        <StyledWrapper {...styles.viewItemWrapper}>
                            <XngZone
                                contentId={id}
                                zoneName="ContentMainHeader03"
                                normalWidth={true}
                                hint="View Item"
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.paginationList}>
                            <XngZone
                                contentId={id}
                                zoneName="ContentMainHeader05"
                                normalWidth={true}
                                hint="Pagination Page"
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                {modalIsOpen && (
                    <StyledWrapper {...styles.filterTabletScreen}>
                        <StyledWrapper {...styles.filterTabletScreenContainer}>
                            <StyledWrapper {...styles.filterTabletScreenHeader}>
                                <Typography {...styles.refineResultsTitle} data-test-selector="productListFilterList">
                                    {translate("Refine Results")}
                                </Typography>
                                <Clickable
                                    onClick={() => toggleFilterListModalHandler(false)}
                                    data-test-selector="expandPLPMobileFilter"
                                    aria-label="Close Product Filters"
                                >
                                    <Icon
                                        src={MenuFocused}
                                        size={20}
                                        color="common.ui6"
                                        css={css`
                                            &:hover {
                                                color: ${color("common.ui13")};
                                            }
                                        `}
                                    />
                                </Clickable>
                            </StyledWrapper>
                            <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
                            <StyledWrapper {...styles.filterTabletScreenActions}>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        clearAllProductFilters();
                                        toggleFilterListModalHandler(false);
                                    }}
                                    aria-label="Close Product Filters"
                                >
                                    {translate("Cancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => toggleFilterListModalHandler(false)}
                                    aria-label="Apply Product Filters"
                                >
                                    {translate("Apply Filters")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                <XngZone contentId={id} zoneName="ContentMainSection" hint="Main Section" />
                <StyledWrapper {...styles.paginationBottom}>
                    <XngZone contentId={id} zoneName="ContentMainSection01" hint="Pagination" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListLayout),
    definition: {
        group: "Product List",
        displayName: "ProductList Layout",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
